import { Action } from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { put, takeLatest } from 'redux-saga/effects';
import { UserModel } from '../models/UserModel';
import { getUserByToken } from './AuthCRUD';

export interface ActionWithPayload<T> extends Action {
  payload?: T;
}

export const actionTypes = {
  Login: '[Login] Action',
  Logout: '[Logout] Action',
  Register: '[Register] Action',
  UserRequested: '[Request User] Action',
  UserLoaded: '[Load User] Auth API',
  SetUser: '[Set User] Action',
  SetTokens: '[Set Tokens] Action',
};

const initialAuthState: IAuthState = {
  user: undefined,
  accessToken: undefined,
  refreshToken: undefined,
  requiresVerification: false,
};

export interface IAuthState {
  user?: UserModel;
  accessToken?: string;
  refreshToken?: string;
  requiresVerification?: boolean;
}

export const reducer = persistReducer(
  { storage, key: 'auth', whitelist: ['user', 'accessToken', 'refreshToken'] },
  (state: IAuthState = initialAuthState, action: ActionWithPayload<IAuthState>) => {
    switch (action.type) {
      case actionTypes.Login: {
        const { accessToken, refreshToken, requiresVerification } = action.payload!;
        return { ...state, accessToken, refreshToken, requiresVerification };
      }

      case actionTypes.Register: {
        const { accessToken, refreshToken, requiresVerification } = action.payload!;
        return { ...state, accessToken, refreshToken, requiresVerification };
      }

      case actionTypes.Logout: {
        return initialAuthState;
      }

      case actionTypes.UserRequested: {
        return { ...state, user: undefined };
      }

      case actionTypes.UserLoaded: {
        const { user } = action.payload!;
        return { ...state, user };
      }

      case actionTypes.SetUser: {
        const { user } = action.payload!;
        return { ...state, user };
      }

      case actionTypes.SetTokens: {
        const { accessToken, refreshToken } = action.payload!;
        return { ...state, accessToken, refreshToken };
      }

      default:
        return state;
    }
  }
);

export const actions = {
  login: (accessToken: string, refreshToken: string, requiresVerification: boolean) => ({
    type: actionTypes.Login,
    payload: { accessToken, refreshToken, requiresVerification },
  }),
  register: (accessToken: string, refreshToken: string, requiresVerification: boolean) => ({
    type: actionTypes.Register,
    payload: { accessToken, refreshToken, requiresVerification },
  }),
  logout: () => ({ type: actionTypes.Logout }),
  requestUser: () => ({ type: actionTypes.UserRequested }),
  fulfillUser: (user: UserModel) => ({ type: actionTypes.UserLoaded, payload: { user } }),
  setUser: (user: UserModel) => ({ type: actionTypes.SetUser, payload: { user } }),
  setTokens: (accessToken: string, refreshToken: string) => ({
    type: actionTypes.SetTokens,
    payload: { accessToken, refreshToken },
  }),
};

export function* saga() {
  yield takeLatest(actionTypes.Login, function* loginSaga() {
    yield put(actions.requestUser());
  });

  yield takeLatest(actionTypes.Register, function* registerSaga() {
    yield put(actions.requestUser());
  });

  yield takeLatest(actionTypes.UserRequested, function* userRequested() {
    const { data: user } = yield getUserByToken();
    yield put(actions.fulfillUser(user));
  });
}
